import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type OperatorMfaStatus, type OperatorTotpSetup } from '@vello/api-client';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Field, Notice, PageHeader, Text, TextInput, Card, Stack, CodeColumns, Page, TapLink } from '@vello/web-theme';

/**
 * Configurações › **Meu acesso** (W8-OPERATORS-AND-SCOPES, D449, ADR-0078; finding A-06). Every operator — no permission
 * word needed — sees its own second-factor state, sets up its OWN authenticator (the way off the team's shared legacy
 * secret, which the owner switches off once everyone has moved: runbook 08) and replaces its recovery codes. A new set of
 * codes is shown ONCE.
 */
export function MyAccessScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v, fail: failText } = useVocab();
  const auth = runtime.operatorAuth;

  const [status, setStatus] = useState<OperatorMfaStatus | undefined>(undefined);
  const [enrollment, setEnrollment] = useState<{ id: string; totp: OperatorTotpSetup } | undefined>(undefined);
  const [code, setCode] = useState('');
  // D502 (Q-005): the CURRENT factor, proved to swap the authenticator — a code of the app in use, or an unused recovery code.
  const [proof, setProof] = useState('');
  const [codes, setCodes] = useState<readonly string[] | undefined>(undefined);
  const [msg, setMsg] = useState<{ tone: 'danger' | 'success'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const res = await auth.mfaStatus();
    if (res.ok) setStatus(res.value);
    else setMsg({ tone: 'danger', text: t('opr.failed', { code: failText(res.error) }) });
  }, [auth, t, failText]);

  useEffect(() => {
    void load();
  }, [load]);

  const fail = (reason: string | undefined, fallback: string): void => setMsg({ tone: 'danger', text: reason === 'MFA_CODE_INVALID' ? t('inv.badCode') : reason === 'MFA_CURRENT_FACTOR_INVALID' || reason === 'MFA_CURRENT_FACTOR_REQUIRED' ? t('my.proofBad') : t('opr.failed', { code: reason ?? fallback }) });

  const start = async (): Promise<void> => {
    setMsg(undefined);
    setCodes(undefined);
    setBusy(true);
    const res = await auth.startMfaEnrollment();
    setBusy(false);
    if (res.ok) setEnrollment({ id: res.value.enrollmentId, totp: res.value.totp });
    else fail(res.error.reasonCode, res.error.code);
  };

  const confirmCode = async (): Promise<void> => {
    if (enrollment === undefined) return;
    setBusy(true);
    const current = proof.trim();
    const res = await auth.confirmMfaEnrollment(enrollment.id, { code: code.trim(), ...(/^\d{6}$/.test(current) ? { currentCode: current } : current !== '' ? { recoveryCode: current } : {}) });
    setBusy(false);
    if (!res.ok) return fail(res.error.reasonCode, res.error.code);
    setEnrollment(undefined);
    setCode('');
    setProof('');
    setCodes(res.value.recoveryCodes);
    await load();
  };

  const regenerate = async (): Promise<void> => {
    setBusy(true);
    const res = await auth.regenerateRecoveryCodes(code.trim());
    setBusy(false);
    if (!res.ok) return fail(res.error.reasonCode, res.error.code);
    setCode('');
    setCodes(res.value.recoveryCodes);
    await load();
  };

  return (
    <Page>
      <PageHeader title={t('my.title')} subtitle={t('my.subtitle')} actions={status?.enrollmentAvailable === true && enrollment === undefined ? <Button variant="primary" onClick={() => void start()} disabled={busy} data-testid="my-enroll">{t('my.enroll')}</Button> : undefined} />
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {status !== undefined && (
        <Card as="section">
          <Stack gap={3}>
            <span aria-label={t('my.state', { state: v('operator.mfa', status.mfa) })}><Badge tone={status.mfa === 'SEALED' ? 'success' : 'warning'}>{v('operator.mfa', status.mfa)}</Badge></span>
            <span>{t('opr.recoveryLeft', { n: status.recoveryCodesRemaining })}</span>
            {status.mfa === 'SHARED_LEGACY' && <Notice tone="warning">{t('my.legacyWarning')}</Notice>}
            {!status.enrollmentAvailable && <Notice tone="neutral">{t('my.unavailable')}</Notice>}
          </Stack>
        </Card>
      )}
      {enrollment !== undefined && (
        <Card as="section" data-testid="my-enrollment">
          <Stack gap={3}>
            <span>{t('inv.step.app')}</span>
            <Text as="code" mono block>{enrollment.totp.setupKey}</Text>
            <TapLink href={enrollment.totp.otpauthUri}>{t('inv.step.link')}</TapLink>
            <Field label={t('inv.field.code')} htmlFor="my-code"><TextInput id="my-code" inputMode="numeric" autoComplete="one-time-code" className="tnum" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
            <Field label={t('my.proof')} htmlFor="my-proof" hint={t('my.proof.hint')}><TextInput id="my-proof" autoComplete="off" className="tnum" value={proof} onChange={(e) => setProof(e.target.value)} /></Field>
  <Button variant="secondary" onClick={() => void confirmCode()} disabled={busy} data-testid="my-confirm">{t('my.confirm')}</Button>
          </Stack>
        </Card>
      )}
      {enrollment === undefined && status?.mfa === 'SEALED' && (
        <Card as="section">
          <Stack gap={3}>
            <Field label={t('my.currentCode')} htmlFor="my-current"><TextInput id="my-current" inputMode="numeric" autoComplete="one-time-code" className="tnum" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
  <Button variant="secondary" onClick={() => void regenerate()} disabled={busy}>{t('my.regenerate')}</Button>
          </Stack>
        </Card>
      )}
      {codes !== undefined && (
        <Card as="section" data-testid="my-codes">
          <Stack gap={3}>
            <strong>{t('inv.codes.title')}</strong>
            <span>{t('inv.codes.body')}</span>
            <CodeColumns>{codes.map((c) => <li key={c}>{c}</li>)}</CodeColumns>
          </Stack>
        </Card>
      )}
    </Page>
  );
}
